import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildCasePackageZip } from '../../../utils/buildCasePackageZip';
import { mvpApi } from '../../../services/api';

export default function DownloadPackageModal({
  open,
  onClose,
  onDownloadSuccess,
  userId,
  sessionId,
  caseDisplayId,
  applicantName,
  merged,
  ex10PdfIsDraft,
  identityPairs,
  contactPairs,
  addressPairs,
  representativePairs,
  notificationPairs,
}) {
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState(null);

  useEffect(() => {
    if (!open) {
      setZipError(null);
      setZipping(false);
    }
  }, [open]);

  const stillNeededCount = useMemo(
    () => (merged || []).filter((s) => !s.uploaded).length,
    [merged],
  );

  const packageIncomplete = stillNeededCount > 0 || ex10PdfIsDraft;
  const approvedSlots = useMemo(
    () => (merged || []).filter((slot) => slot.uploaded && String(slot.reviewStatus || '').toLowerCase() === 'accepted'),
    [merged],
  );

  const handleDownloadZip = useCallback(async () => {
    if (!userId || !sessionId) {
      setZipError('Missing case identifiers.');
      return;
    }
    setZipping(true);
    setZipError(null);
    try {
      const downloadFile = async (documentId) => {
        const id = documentId != null ? String(documentId) : '';
        if (!id) throw new Error('Missing document id');
        const res = await mvpApi.downloadAdminCaseDocumentFile(userId, sessionId, id);
        return res.data;
      };
      const { blob, filename } = await buildCasePackageZip({
        downloadFile,
        caseDisplayId,
        isDraft: ex10PdfIsDraft,
        applicantName,
        identityPairs,
        contactPairs,
        addressPairs,
        representativePairs,
        notificationPairs,
        merged,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking immediately can cancel the download in some browsers; defer cleanup.
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
      onDownloadSuccess?.(filename);
      onClose?.();
    } catch (e) {
      setZipError('Could not build ZIP package.');
    } finally {
      setZipping(false);
    }
  }, [
    userId,
    sessionId,
    caseDisplayId,
    ex10PdfIsDraft,
    applicantName,
    identityPairs,
    contactPairs,
    addressPairs,
    representativePairs,
    notificationPairs,
    merged,
    onClose,
    onDownloadSuccess,
  ]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-3 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-package-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[calc(100dvh-1.5rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 shrink-0 sm:px-6 sm:pt-6 sm:pb-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded-xl">
              <i className="ri-folder-zip-line text-slate-600 text-lg" />
            </div>
            <div className="min-w-0">
              <h2 id="download-package-title" className="text-sm font-semibold text-slate-900">Download Package</h2>
              <p className="text-[11px] text-slate-400 font-mono break-all">{caseDisplayId || 'CASE'}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer transition-colors rounded-lg hover:bg-slate-100"
            aria-label="Close"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {!packageIncomplete ? null : (
          <div className="mx-4 mb-3 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 shrink-0 sm:mx-6">
            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-alert-line text-amber-500 text-sm" />
            </div>
            <p className="text-xs text-amber-700 leading-relaxed">
              <span className="font-semibold">Package is incomplete.</span>{' '}
              {stillNeededCount} document{stillNeededCount !== 1 ? 's' : ''} still needed.
            </p>
          </div>
        )}

        <div className="px-4 pb-4 overflow-y-auto flex-1 space-y-4 sm:px-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Included in package</p>
            <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              <div className="flex items-start gap-3 px-3 py-2.5 sm:items-center sm:px-4">
                <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg shrink-0">
                  <i className="ri-file-text-line text-emerald-600 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-800">EX-10 Application Form</p>
                  <p className="text-[10px] text-slate-400">Auto-generated PDF · Spanish (official)</p>
                </div>
                <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                  Included
                </span>
              </div>

              {approvedSlots.length === 0 ? (
                <div className="px-4 py-3">
                  <p className="text-xs text-slate-400 italic">No approved documents yet</p>
                </div>
              ) : (
                approvedSlots.map((slot) => (
                  <div key={slot.id} className="flex items-start gap-3 px-3 py-2.5 sm:items-center sm:px-4">
                    <div className="w-7 h-7 flex items-center justify-center bg-slate-50 rounded-lg shrink-0">
                      <i className="ri-file-line text-slate-400 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{slot.name}</p>
                      <p className="text-[10px] text-slate-400 break-words">
                        {slot.files?.[0]?.filename || 'Uploaded document'}
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                      Accepted
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Document manifest</p>
            <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {(merged || []).map((slot) => {
                const status = String(slot.reviewStatus || '').toLowerCase();
                const accepted = slot.uploaded && status === 'accepted';
                const pending = slot.uploaded && status !== 'accepted' && status !== 'rejected';
                return (
                  <div key={slot.id} className="flex items-start gap-3 px-3 py-2.5 sm:items-center sm:px-4">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      {accepted ? (
                        <i className="ri-checkbox-circle-fill text-emerald-500 text-base" />
                      ) : pending ? (
                        <i className="ri-time-line text-amber-400 text-base" />
                      ) : (
                        <i className="ri-checkbox-blank-circle-line text-slate-300 text-base" />
                      )}
                    </div>
                    <span className="flex-1 min-w-0 text-xs text-slate-700 break-words sm:truncate">{slot.name}</span>
                    <span
                      className={`text-[10px] font-medium shrink-0 ${
                        accepted ? 'text-emerald-600' : pending ? 'text-amber-500' : 'text-slate-400'
                      }`}
                    >
                      {accepted ? 'Approved' : pending ? 'Pending review' : 'Missing'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {zipError ? <p className="text-xs text-rose-600">{zipError}</p> : null}
        </div>

        <div className="px-4 pb-4 pt-3 shrink-0 border-t border-slate-100 flex flex-col gap-2 sm:flex-row sm:px-6 sm:pb-6">
          <button
            type="button"
            onClick={handleDownloadZip}
            disabled={zipping}
            className="min-h-[44px] flex-1 flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {zipping ? (
              <>
                <i className="ri-loader-4-line animate-spin" /> Building ZIP...
              </>
            ) : (
              <>
                <i className="ri-download-2-line" /> Download ZIP
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={zipping}
            className="min-h-[44px] px-4 text-sm font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
